import { useState, useCallback, useRef } from "react";
import "../../../styles/admin/UserPreview.css";
import { API } from '../../../api';

export default function UserPreview({ item, onDropImage, originalAvailable, showOriginal, onToggleOriginal }) {
	console.log("UserPreview item:", item);
	const [dragOver, setDragOver] = useState(false);
	const dragCounter = useRef(0);

	const handleDragOver = (e) => {
		if (!onDropImage) return;
		e.preventDefault();
		e.stopPropagation();
		setDragOver(true);
	};

	const handleDragEnter = (e) => {
		if (!onDropImage) return;
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current += 1;
		setDragOver(true);
	};

	const handleDragLeave = (e) => {
		if (!onDropImage) return;
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current = Math.max(0, dragCounter.current - 1);
		if (dragCounter.current === 0) setDragOver(false);
	};

	const handleDrop = useCallback((e) => {
		if (!onDropImage) return;
		e.preventDefault();
		e.stopPropagation();
		dragCounter.current = 0;
		setDragOver(false);
		const file = e.dataTransfer?.files?.[0];
		if (!file) return;
		const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp", "image/avif"];
		if (!validTypes.includes(file.type)) return;

		onDropImage(file);
	}, [onDropImage]);

	if (!item) return (
		<div className="user-preview-empty">
			<div className="empty-state">
				<span className="empty-icon">👤</span>
				<p>Sélectionnez un utilisateur pour voir l'aperçu</p>
			</div>
		</div>
	);

	const getImageSrc = () => {
		const image_source = (item.image_source || "").toLowerCase();
		const image_name = item.image_name || "";
		const image_extension = item.image_extension || "";
		const hasLoadError = item.urlLoadError === true;

		if (image_source === "web" && image_name && hasLoadError) return null;
		if (image_source === "preview" && image_name) return image_name;
		if (image_source === "web" && image_name && !hasLoadError) return image_name;
		if (image_source === "local" && image_name && image_extension) return `${API.API_LINK}/file/pictures/avatars/source/${image_name}.${image_extension}`;
		if (!image_source && image_name && image_extension) return `${API.API_LINK}/file/pictures/avatars/source/${image_name}.${image_extension}`;

		return null;
	};

	const getRoleColor = (role) => {
		const roleColors = {
			owner: "#e74c3c",
			admin: "#3498db",
			moderator: "#f39c12",
			user: "#27ae60"
		};
		return roleColors[role] || "#95a5a6";
	};

	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString("fr-FR", {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit"
		});
	};

	const formatPhoneNumber = (phone) => {
		if (!phone) return phone;
		const cleaned = phone.replace(/\D/g, "");
		if (!cleaned) return phone;
		if (cleaned[0] !== "0") return cleaned[0] + " " + cleaned.slice(1).replace(/(\d{2})(?=\d)/g, "$1 ");
		return cleaned.replace(/(\d{2})(?=\d)/g, "$1 ");
	};

	const imageSrc = getImageSrc();
	const hasUrlError = item.urlLoadError === true;
	const isWebType = (item.image_source || "").toLowerCase() === "web";
	const roleColor = getRoleColor(item.role_slug);

	return (
		<div 
			className={`user-preview-container${onDropImage ? " drop-enabled" : ""}${dragOver ? " drag-over" : ""}`}
			onDragOver={handleDragOver}
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			{/* Header avec gradient */}
			<div className="preview-header" style={{ background: `linear-gradient(135deg, ${roleColor}99 0%, ${roleColor}33 100%)` }}>
				<div className="header-content">
					<h2 className="preview-title">Profil Utilisateur</h2>
					<span className="role-badge" style={{ backgroundColor: roleColor }}>
						{item.role_slug?.toUpperCase()}
					</span>
				</div>
			</div>

			{/* Overlay drag & drop global */}
			{onDropImage && dragOver && (
				<div className="drop-overlay">
					<div className="drop-overlay-content">
						<span className="drop-icon">📤</span>
						<p>Déposez l'image (PNG, JPG, GIF, WEBP, AVIF)</p>
					</div>
				</div>
			)}

			{/* Avatar - En haut centré */}
			<div className="avatar-section">
				<div className="avatar-wrapper">
					{imageSrc && !hasUrlError ? (
						<div 
							className="avatar-image"
							style={{ backgroundImage: `url(${imageSrc})` }}
						>
							<div className="avatar-overlay">
								{onDropImage && <span className="upload-hint">📤 Déposer ici</span>}
							</div>
						</div>
					) : (
						<div className="avatar-placeholder">
							<span className="avatar-icon">👤</span>
							{hasUrlError && isWebType && <p>❌ Erreur</p>}
							{onDropImage && <p className="drop-text">Déposer une image</p>}
						</div>
					)}
				</div>
			</div>

			{/* Contenu principal - Infos en grille 2 colonnes */}
			<div className="preview-content">
				{/* Nom complet */}
				<div className="info-card">
					<div className="info-header">
						<h3 className="info-title">👤 Identité</h3>
					</div>
					<div className="info-body">
						<div className="full-name">
							<span className="first-name">{item.first_name}</span>
							<span className="last-name">{item.last_name}</span>
						</div>
						<div className="login-info">
							<p className="label">Login:</p>
							<p className="value" title={item.login_metadata?.login}>{item.login_metadata?.login || "N/A"}</p>
						</div>
					</div>
				</div>

				{/* Contact */}
				<div className="info-card">
					<div className="info-header">
						<h3 className="info-title">📧 Contact</h3>
					</div>
					<div className="info-body">
						<p className="label">Email:</p>
						<p className="value email-value" title={item.email}>{item.email}</p>
						{item.phone && (
							<>
								<p className="label">Téléphone:</p>
								<p className="value">{formatPhoneNumber(item.phone)}</p>
							</>
						)}
					</div>
				</div>

				{/* Sécurité */}
				<div className="info-card">
					<div className="info-header">
						<h3 className="info-title">🔒 Sécurité</h3>
					</div>
					<div className="info-body">
						<div className="security-item">
							<span className="security-label">2FA:</span>
							<span className={`security-value ${item.login_metadata?.twoFA_enabled ? "enabled" : "disabled"}`}>
								{item.login_metadata?.twoFA_enabled ? "✅ Activé" : "❌ Désactivé"}
							</span>
						</div>
						<div className="security-item">
							<span className="security-label">Verrouillage:</span>
							<span className={`security-value ${item.login_metadata?.lock_count > 0 ? "locked" : "active"}`}>
								{item.login_metadata?.lock_count > 0 ? `🔐 Verrouillé (${item.login_metadata.lock_count})` : "✅ Actif"}
							</span>
						</div>
					</div>
				</div>

				{/* Dates */}
				<div className="info-card">
					<div className="info-header">
						<h3 className="info-title">📅 Dates</h3>
					</div>
					<div className="info-body">
						<p className="label">Créé:</p>
						<p className="value date-value">{formatDate(item.created_when)}</p>
						<p className="label">Modifié:</p>
						<p className="value date-value">{formatDate(item.updated_when)}</p>
					</div>
				</div>

				{/* ID */}
				<div className="info-card info-card-small">
					<p className="label">UUID:</p>
					<p className="value uuid-value" title={item.uuid}>{item.uuid?.slice(0, 12)}...</p>
				</div>
			</div>

			{/* Options */}
			{onDropImage && (
				<div className="preview-options">
					<div className="drop-hint">💡 Glissez-déposez une image pour changer l'avatar</div>
				</div>
			)}

			{originalAvailable && (
				<div className="preview-toggle">
					<label className="toggle-switch">
						<input type="checkbox" checked={!!showOriginal} onChange={onToggleOriginal} />
						<span className="toggle-slider"></span>
						<span className="toggle-label">Voir la version originale</span>
					</label>
				</div>
			)}
		</div>
	);
}
